import React, { useCallback, useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

function Thumb({ id, alt }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/phase4/photos/${id}/file`, { params: { variant: "thumb" }, responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  return src ? <img src={src} alt={alt} className="h-24 w-32 rounded-md object-cover" /> : <div className="h-24 w-32 rounded-md bg-erf-borderCream" aria-hidden="true" />;
}

export function PhotoPanel({ pid, logs }) {
  const [rows, setRows] = useState([]);
  const [caption, setCaption] = useState("");
  const [logId, setLogId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase4/photos/projects/${pid}`).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const params = { filename: file.name, caption, ...(logId ? { daily_log_id: logId } : {}) };
    api.post(`/phase4/photos/projects/${pid}`, file, { params, headers: { "Content-Type": "application/octet-stream" } })
      .then(() => { setCaption(""); setError(null); load(); })
      .catch((err) => setError(errMsg(err, "Could not upload the photo.")))
      .finally(() => { setBusy(false); e.target.value = ""; });
  };
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase4-photo-panel">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Site photos</h2>
        <p className="text-xs text-erf-dark/80">Location and camera details are removed from every copy people can view. The original stays private.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label>Caption<Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1 h-11" data-testid="phase4-photo-caption" /></label>
          <label>Daily log (optional)<select value={logId} onChange={(e) => setLogId(e.target.value)} className="mt-1 block h-11 rounded-md border border-erf-borderCream bg-white px-3" data-testid="phase4-photo-log">
            <option value="">None</option>{(logs || []).map((l) => <option key={l.id} value={l.id}>{l.log_date}</option>)}
          </select></label>
          <label className="inline-flex h-11 cursor-pointer items-center rounded-md bg-erf-gold px-4 text-erf-dark hover:bg-erf-goldHover">
            <Camera className="w-4 h-4 mr-1.5" aria-hidden="true" />{busy ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="sr-only" onChange={upload} disabled={busy} data-testid="phase4-photo-input" />
          </label>
        </div>
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase4-photo-error">{error}</p>}
        <ul className="flex flex-wrap gap-3" data-testid="phase4-photo-list">
          {rows.length === 0 && <li>No photos yet.</li>}
          {rows.map((p) => (
            <li key={p.id} className="w-32" data-testid={`phase4-photo-${p.id}`}>
              <Thumb id={p.id} alt={p.caption || "Site photo"} />
              <span className="block truncate text-xs mt-1">{p.caption || "No caption"}{p.daily_log_id ? " · log" : ""}{p.punch_item_id ? " · punch" : ""}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

const STATUS = { expired: "Expired", expiring: "Expires within 30 days", current: "Current", no_expiry: "No expiry" };

export function CompliancePanel({ pid, editor }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ kind: "permit", title: "", reference: "", expires_on: "" });
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase4/compliance/projects/${pid}`).then((r) => setRows(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase4-compliance-panel">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Compliance binder</h2>
        {editor && (
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post(`/phase4/compliance/projects/${pid}`, { ...f, expires_on: f.expires_on || null }).then(() => setF({ ...f, title: "", reference: "", expires_on: "" }))); }}>
            <label>Type<select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="mt-1 block h-11 rounded-md border border-erf-borderCream bg-white px-3" data-testid="phase4-comp-kind"><option value="permit">Permit</option><option value="inspection">Inspection</option><option value="certification">Certification</option></select></label>
            <label>Title<Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1 h-11" data-testid="phase4-comp-title" /></label>
            <label>Reference<Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1 h-11 w-32" data-testid="phase4-comp-ref" /></label>
            <label>Expires<Input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className="mt-1 h-11" data-testid="phase4-comp-expires" /></label>
            <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase4-comp-add">Add</Button>
            <Button type="button" variant="outline" className="h-11 border-erf-borderCream" onClick={() => api.post("/phase4/compliance/reminders/run").then((r) => setMsg(`${r.data.created} new reminder(s). Reminders appear here only; nothing is emailed.`)).catch((e) => setError(errMsg(e, "Could not check reminders.")))} data-testid="phase4-comp-reminders">Check expiries</Button>
          </form>
        )}
        {msg && <p role="status" data-testid="phase4-comp-msg">{msg}</p>}
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase4-comp-error">{error}</p>}
        <ul className="space-y-1">
          {rows.length === 0 && <li>Nothing in the binder yet.</li>}
          {rows.map((r) => <li key={r.id} className={r.status === "expired" ? "text-[#991B1B]" : ""} data-testid={`phase4-comp-${r.id}`}><strong>{r.title}</strong> · {r.kind}{r.reference && ` · ${r.reference}`}{r.expires_on && ` · expires ${r.expires_on}`} · {STATUS[r.status]}</li>)}
        </ul>
      </CardContent>
    </Card>
  );
}
